import { AddressObject } from '@/types/store';
import { GeocodeResult } from '@/utils/googlePlaces';

/**
 * Utility functions extracted from AddressFormatter.vue for use in composables
 * This provides consistent address formatting across the application
 */

export interface FormattedAddress {
  streetWithApt: string;
  cityStateZip: string;
  isValid: boolean;
  fullFormatted?: string;
  geocodedAddress?: AddressObject | null;
}

/**
 * Format address items into a structured address object
 * Handles both string[] and AddressObject formats
 */
export async function formatAddress(
  addressItems: string[] | AddressObject | null | undefined
): Promise<FormattedAddress> {
  if (!addressItems) {
    return {
      streetWithApt: 'Unavailable',
      cityStateZip: 'Unavailable',
      isValid: false
    };
  }

  const geocodedAddress: AddressObject | null = null;
  // Intentionally avoid auto-geocoding here; formatting should be pure.

  const addressSource = geocodedAddress || addressItems;

  const streetWithApt = formatStreetWithApt(addressSource);
  const cityStateZip = formatCityStateZip(addressSource);
  const isValid = isValidAddress(addressSource, geocodedAddress);

  // Create a full formatted address string for details array
  const fullFormatted = isValid 
    ? `${streetWithApt}${streetWithApt !== 'Unavailable' && cityStateZip !== 'Unavailable' ? ', ' : ''}${cityStateZip}`
    : 'Unavailable';

  return {
    streetWithApt,
    cityStateZip,
    isValid,
    fullFormatted: fullFormatted !== 'Unavailable, Unavailable' ? fullFormatted : 'Unavailable',
    geocodedAddress
  };
}

/**
 * Format street address with apartment info
 */
function formatStreetWithApt(addressSource: string[] | AddressObject): string {
  if (Array.isArray(addressSource)) {
    const [street, apt] = addressSource;
    const combined = street ? (apt ? `${street}, ${apt}` : street) : '';
    return combined || 'Unavailable';
  } else if ('geocode' in addressSource) {
    const addressComponents = addressSource.geocode.address_components || [];
    const streetNumberComponent = addressComponents.find(component =>
      component.types.includes('street_number')
    );
    const streetComponent = addressComponents.find(component =>
      component.types.includes('route')
    );
    const aptComponent = addressComponents.find(component =>
      component.types.includes('subpremise')
    );
    
    const streetNumber = streetNumberComponent ? streetNumberComponent.long_name : '';
    const street = streetComponent ? streetComponent.long_name : '';
    const apt = aptComponent ? aptComponent.long_name : '';
    
    const combined = (streetNumber || street)
      ? (apt ? `${streetNumber} ${street}, ${apt}` : `${streetNumber} ${street}`)
      : '';
    
    return combined.trim() || 'Unavailable';
  } else {
    return 'Unavailable';
  }
}

/**
 * Format city, state, and zip
 */
function formatCityStateZip(addressSource: string[] | AddressObject): string {
  if (Array.isArray(addressSource)) {
    // Array structure [street, apt, city, state, zipcode] doesn't include country
    // Without country, location data is considered incomplete
    return 'Unavailable';
  } else if ('geocode' in addressSource) {
    const addressComponents = addressSource.geocode.address_components || [];

    const countryComponent = addressComponents.find(component =>
      component.types.includes('country')
    );
    const stateComponent = addressComponents.find(component =>
      component.types.includes('administrative_area_level_1')
    );
    const cityComponent = addressComponents.find(component =>
      component.types.includes('locality')
    );

    const country = countryComponent ? countryComponent.long_name : '';
    const state = stateComponent ? stateComponent.short_name : '';
    const city = cityComponent ? cityComponent.long_name : '';

    // Without country, we don't have complete location info
    if (!country) return 'Unavailable';
    // Without state, data is considered incomplete
    if (!state) return 'Unavailable';

    // Display format: "city, state country" or "state, country"
    return city ? `${city}, ${state} ${country}` : `${state}, ${country}`;
  } else {
    return 'Unavailable';
  }
}

/**
 * Validate if address is complete and usable
 */
function isValidAddress(addressSource: string[] | AddressObject, geocodedAddress?: AddressObject | null): boolean {
  if (Array.isArray(addressSource)) {
    return (addressSource.length >= 5 && addressSource.some(item => !!item)) || geocodedAddress !== null;
  } else if ('geocode' in addressSource) {
    return !!addressSource.geocode.formatted_address;
  } else {
    return false;
  }
}

/**
 * Extract a simple address string for backward compatibility
 * This can be used where a single address string is needed
 */
export function extractSimpleAddress(addressItems: string[] | AddressObject | null | undefined): string {
  if (!addressItems) return '';

  if (Array.isArray(addressItems)) {
    // If array form ever carries suite separately, attempt to infer and include.
    // Common patterns: [street, apt, city, state, country] or similar.
    const parts = addressItems.filter(Boolean);
    return parts.join(', ');
  } else {
    // Handle AddressObject - check for geocoded address first, then fallbacks
    const addressObj = addressItems as AddressObject;
    const base = addressObj.geocode?.formatted_address
      || addressObj.description
      || addressObj.name
      || '';
    const suite = (addressObj as any).suite as string | undefined;
    // Include suite in a stable, human-visible way so equality checks catch apt differences
    return suite && suite.trim() ? `${base} Apt ${suite.trim()}` : base;
  }

  return '';
}

/**
 * Create address detail object for use in details arrays
 * This matches the format expected by AlgoliaTransformedUser
 */
export async function createAddressDetail(
  addressItems: string[] | AddressObject | null | undefined,
  fieldId: string = 'address',
  fieldName: string = 'Address'
): Promise<{ label: string; value: string; type: string; original_id: string } | null> {
  const formatted = await formatAddress(addressItems);
  
  if (!formatted.isValid) {
    return null; // Don't include invalid addresses in details
  }

  return {
    label: fieldName,
    value: formatted.fullFormatted || 'Unavailable',
    type: 'address',
    original_id: fieldId
  };
}